import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, MapPin, Calendar, User, MessageCircle, Image as ImageIcon, Trash2, Eye, EyeOff, Utensils, Link2, CheckCircle, XCircle, HelpCircle, CopyPlus } from 'lucide-react';
import api from '../../utils/api';
import { getDishDisplayName } from '../../utils/dishDisplay';
import { DEFAULT_AVATAR } from '../../utils/constants';
import { useSnackbar } from '../../contexts/SnackbarContext';
import './MosqueDetails.scss';

const MosqueDetails = () => {
    const { mosqueID } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { success, error, confirm } = useSnackbar();
    const [deletingIndex, setDeletingIndex] = useState(null);
    const [deletingCommentId, setDeletingCommentId] = useState(null);
    const [hidingCommentId, setHidingCommentId] = useState(null);
    const [hidingImageIndex, setHidingImageIndex] = useState(null);
    const [linkingRestaurantId, setLinkingRestaurantId] = useState(null);
    const [copyingForYear, setCopyingForYear] = useState(false);
    const [copyOverlayOpen, setCopyOverlayOpen] = useState(false);
    const [copyOverlayForm, setCopyOverlayForm] = useState({ name: '', dish: '', description: '' });
    const [mosque, setMosque] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [clarificationModal, setClarificationModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectSendEmail, setRejectSendEmail] = useState(true);
    const [clarificationRemarks, setClarificationRemarks] = useState('');

    useEffect(() => {
        fetchMosque();
    }, [mosqueID]);

    const fetchMosque = async () => {
        try {
            const response = await api.get(`/admin/mosques/${mosqueID}`);
            if (response.data.success) {
                setMosque(response.data.mosque);
            }
        } catch (err) {
            console.error('Error fetching mosque:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        confirm('Delete this comment?', async () => {
            setDeletingCommentId(commentId);
            try {
                const response = await api.delete(`/admin/mosques/${mosqueID}/comments`, {
                    data: { commentId }
                });
                if (response.data.success) {
                    setMosque(prev => ({
                        ...prev,
                        comments: prev.comments.filter(c => c._id !== commentId)
                    }));
                    success('Comment deleted');
                }
            } catch (err) {
                error(err.response?.data?.message || 'Failed to delete comment');
            } finally {
                setDeletingCommentId(null);
            }
        });
    };

    const handleToggleCommentHide = async (commentId) => {
        setHidingCommentId(commentId);
        try {
            const response = await api.patch(`/admin/mosques/${mosqueID}/comments/${commentId}/hide`);
            if (response.data.success) {
                setMosque(prev => ({
                    ...prev,
                    comments: prev.comments.map(c =>
                        c._id === commentId ? { ...c, hidden: response.data.hidden } : c
                    )
                }));
                success(response.data.hidden ? 'Comment hidden' : 'Comment unhidden');
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to toggle visibility');
        } finally {
            setHidingCommentId(null);
        }
    };

    const handleToggleImageHide = async (index) => {
        setHidingImageIndex(index);
        try {
            const response = await api.patch(`/admin/mosques/${mosqueID}/images/${index}/hide`);
            if (response.data.success) {
                setMosque(prev => ({
                    ...prev,
                    images: prev.images.map((img, i) =>
                        i === index ? { ...img, hidden: response.data.hidden } : img
                    )
                }));
                success(response.data.hidden ? 'Image hidden' : 'Image unhidden');
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to toggle visibility');
        } finally {
            setHidingImageIndex(null);
        }
    };

    const handleDeleteImage = async (index) => {
        confirm('Delete this image?', async () => {
            setDeletingIndex(index);
            try {
                const response = await api.delete(`/admin/mosques/${mosqueID}/images`, {
                    data: { imageIndex: index }
                });
                if (response.data.success) {
                    setMosque(prev => ({
                        ...prev,
                        images: prev.images.filter((_, i) => i !== index)
                    }));
                    success('Image deleted');
                }
            } catch (err) {
                error(err.response?.data?.message || 'Failed to delete image');
            } finally {
                setDeletingIndex(null);
            }
        });
    };

    const handleLinkRestaurant = async (restaurant) => {
        const restaurantID = typeof restaurant === 'string' ? restaurant : restaurant.restaurantID;
        setLinkingRestaurantId(restaurantID);
        try {
            const response = await api.patch(`/admin/mosques/${mosqueID}/link-restaurant`, { linkedRestaurantID: restaurantID });
            if (response.data.success) {
                const linked = typeof restaurant === 'object' ? restaurant : { restaurantID, name: '', district: '' };
                setMosque(prev => ({ ...prev, linkedRestaurantID: restaurantID, linkedRestaurant: linked, nearbyRestaurants: (prev.nearbyRestaurants || []).filter(r => r.restaurantID !== restaurantID) }));
                success('Restaurant linked');
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to link restaurant');
        } finally {
            setLinkingRestaurantId(null);
        }
    };

    const handleUnlinkRestaurant = async () => {
        setLinkingRestaurantId('unlink');
        try {
            const response = await api.patch(`/admin/mosques/${mosqueID}/link-restaurant`, { linkedRestaurantID: null });
            if (response.data.success) {
                setMosque(prev => ({ ...prev, linkedRestaurantID: undefined, linkedRestaurant: null }));
                success('Restaurant unlinked');
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to unlink restaurant');
        } finally {
            setLinkingRestaurantId(null);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (!mosque || newStatus === mosque.status) return;
        setUpdatingStatus(true);
        try {
            const updatePayload = { status: newStatus };
            if (newStatus === 'available') updatePayload.statusNote = '';
            const response = await api.put(`/admin/mosques/${mosqueID}`, updatePayload);
            if (response.data.success) {
                setMosque(prev => ({ ...prev, ...response.data.mosque }));
                success('Status updated successfully');
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to update status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleReject = async () => {
        setUpdatingStatus(true);
        try {
            const response = await api.post(`/admin/mosques/${mosqueID}/reject`, {
                reason: rejectReason,
                sendEmail: rejectSendEmail
            });
            if (response.data.success) {
                setMosque(response.data.mosque);
                setRejectModal(false);
                setRejectReason('');
                success('Mosque rejected' + (rejectSendEmail ? ', email sent' : ''));
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to reject');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const currentYear = new Date().getFullYear();
    const canCopyForYear = mosque && mosque.year !== currentYear;

    const DISH_OPTIONS = ['Biriyani', 'Neychoru', 'Pathiri', 'Snacks', 'Jeeraka Kanji ജീരക കഞ്ഞി', 'Other'];

    const openCopyOverlay = () => {
        if (!mosque) return;
        setCopyOverlayForm({
            name: mosque.name || '',
            dish: mosque.dish || '',
            description: mosque.description || ''
        });
        setCopyOverlayOpen(true);
    };

    const closeCopyOverlay = () => {
        setCopyOverlayOpen(false);
        setCopyOverlayForm({ name: '', dish: '', description: '' });
    };

    const handleCopyForYear = async (overrides = {}) => {
        if (!mosqueID || !canCopyForYear) return;
        setCopyingForYear(true);
        try {
            const response = await api.post(`/admin/mosques/${mosqueID}/copy-for-year`, {
                targetYear: currentYear,
                overrides: {
                    name: overrides.name || undefined,
                    dish: overrides.dish || undefined,
                    description: overrides.description !== undefined ? overrides.description : undefined
                }
            });
            if (response.data.success) {
                success(`Mosque added to ${currentYear} list (${response.data.mosque?.mosqueID})`);
                closeCopyOverlay();
                fetchMosque();
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to copy mosque');
        } finally {
            setCopyingForYear(false);
        }
    };

    const handleCopyOverlaySubmit = () => {
        handleCopyForYear(copyOverlayForm);
    };

    const handleClarification = async () => {
        setUpdatingStatus(true);
        try {
            const response = await api.post(`/admin/mosques/${mosqueID}/clarification`, {
                remarks: clarificationRemarks
            });
            if (response.data.success) {
                setMosque(prev => ({
                    ...prev,
                    clarificationRequested: response.data.mosque?.clarificationRequested || {
                        at: new Date().toISOString(),
                        remarks: clarificationRemarks
                    }
                }));
                setClarificationModal(false);
                setClarificationRemarks('');
                success('Clarification email sent and saved');
            }
        } catch (err) {
            error(err.response?.data?.message || 'Failed to send clarification');
        } finally {
            setUpdatingStatus(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading mosque details...</div>;
    }

    if (!mosque) {
        return <div className="error">Mosque not found</div>;
    }

    return (
        <div className="mosque-details">
            <div className="details-header">
                <button className="back-button" onClick={() => navigate(location.state?.returnTo || '/mosques')}>
                    <ArrowLeft size={20} />
                    Back
                </button>
                <div className="details-header__actions">
                    {canCopyForYear && (
                        <button
                            className="copy-year-button"
                            onClick={openCopyOverlay}
                            disabled={copyingForYear}
                            title={`Add to ${currentYear} list`}
                        >
                            <CopyPlus size={18} />
                            {copyingForYear ? 'Adding...' : `Add to ${currentYear}`}
                        </button>
                    )}
                    <button className="edit-button" onClick={() => navigate(`/mosques/${mosqueID}/edit`, { state: { returnTo: location.state?.returnTo || `/mosques/${mosqueID}` } })}>
                        <Edit size={18} />
                        Edit
                    </button>
                </div>
            </div>

            <div className="details-content">
                <div className="section">
                    <h2>{mosque.name}</h2>
                    <div className="info-grid">
                        <div className="info-item">
                            <strong>Mosque ID:</strong> {mosque.mosqueID}
                        </div>
                        <div className="info-item">
                            <strong>District:</strong> {mosque.district}
                        </div>
                        <div className="info-item">
                            <strong>State:</strong> {mosque.state}
                        </div>
                        <div className="info-item">
                            <strong>Dish:</strong> {getDishDisplayName(mosque.dish)}
                        </div>
                        <div className="info-item info-item--status">
                            <strong>Status:</strong>
                            <span className={`status-badge ${mosque.status}`}>
                                {mosque.status}
                            </span>
                            {mosque.status === 'pending' ? (
                                <div className="pending-actions">
                                    <button
                                        type="button"
                                        className="status-action-btn available"
                                        onClick={() => handleStatusChange('available')}
                                        disabled={updatingStatus}
                                    >
                                        <CheckCircle size={16} />
                                        Available
                                    </button>
                                    <button
                                        type="button"
                                        className="status-action-btn reject"
                                        onClick={() => setRejectModal(true)}
                                        disabled={updatingStatus}
                                    >
                                        <XCircle size={16} />
                                        Reject
                                    </button>
                                    <button
                                        type="button"
                                        className="status-action-btn clarification"
                                        onClick={() => setClarificationModal(true)}
                                        disabled={updatingStatus}
                                        title={mosque.clarificationRequested?.at ? `Clarification already sent on ${new Date(mosque.clarificationRequested.at).toLocaleDateString()}. Click to send again.` : 'Ask for Clarification'}
                                    >
                                        <HelpCircle size={16} />
                                        {mosque.clarificationRequested?.at ? 'Request Again' : 'Ask for Clarification'}
                                    </button>
                                </div>
                            ) : (
                                <select
                                    value={mosque.status}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                    disabled={updatingStatus}
                                    className="status-select"
                                    title="Change status"
                                >
                                    <option value="available">Available</option>
                                    <option value="unavailable">Unavailable</option>
                                    <option value="pending">Pending</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            )}
                        </div>
                        <div className="info-item">
                            <strong>Verified:</strong>
                            {mosque.verified ? (
                                <span className="verified-badge">Yes</span>
                            ) : (
                                <span className="unverified-badge">No</span>
                            )}
                        </div>
                        {mosque.linkedRestaurant && (
                            <div className="info-item linked-restaurant">
                                <strong><Utensils size={16} /> Linked Restaurant:</strong>
                                <button type="button" className="link-text" onClick={() => navigate(`/restaurants/${mosque.linkedRestaurantID}`)}>
                                    {mosque.linkedRestaurant.name || mosque.linkedRestaurantID}
                                </button>
                                <button type="button" className="unlink-btn" onClick={handleUnlinkRestaurant} disabled={linkingRestaurantId === 'unlink'} title="Unlink restaurant">
                                    Unlink
                                </button>
                            </div>
                        )}
                    </div>
                    {mosque.statusNote && (
                        <div className="info-item status-note">
                            <strong>Status note:</strong> {mosque.statusNote}
                        </div>
                    )}
                    {mosque.clarificationRequested?.at && (
                        <div className="info-item clarification-requested">
                            <strong><HelpCircle size={16} /> Clarification requested:</strong>
                            <span>{new Date(mosque.clarificationRequested.at).toLocaleString()}</span>
                            {mosque.clarificationRequested.remarks && (
                                <p className="clarification-remarks">{mosque.clarificationRequested.remarks}</p>
                            )}
                        </div>
                    )}
                    {mosque.nearbyRestaurants && mosque.nearbyRestaurants.length > 0 && (
                        <div className="info-item nearby-restaurants">
                            <strong><Link2 size={16} /> Nearby restaurants (same location):</strong>
                            <div className="nearby-list">
                                {mosque.nearbyRestaurants.map(r => (
                                    <span key={r.restaurantID} className="nearby-item">
                                        <button type="button" className="link-text" onClick={() => navigate(`/restaurants/${r.restaurantID}`)}>
                                            {r.name} ({r.restaurantID})
                                        </button>
                                        <button type="button" className="link-btn-small" onClick={() => handleLinkRestaurant(r)} disabled={linkingRestaurantId === r.restaurantID}>
                                            {linkingRestaurantId === r.restaurantID ? 'Linking...' : 'Link'}
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {!mosque.linkedRestaurant && mosque.location?.latitude && (
                        <div className="info-item">
                            <button type="button" className="add-restaurant-link" onClick={() => navigate('/restaurants/add', { state: { suggestMosque: mosque } })}>
                                <Utensils size={16} /> Add restaurant at this location
                            </button>
                        </div>
                    )}
                </div>

                {mosque.description && (
                    <div className="section">
                        <h3>Description</h3>
                        <p>{mosque.description}</p>
                    </div>
                )}

                {mosque.location && (
                    <div className="section">
                        <h3>Location</h3>
                        <div className="info-item">
                            <MapPin size={18} />
                            <a href={mosque.googleMapsLink} target="_blank" rel="noopener noreferrer">
                                View on Google Maps
                            </a>
                        </div>
                        {mosque.location.latitude && (
                            <div className="info-item">
                                <strong>Coordinates:</strong> {mosque.location.latitude}, {mosque.location.longitude}
                            </div>
                        )}
                    </div>
                )}

                {mosque.contributor && (
                    <div className="section">
                        <h3>Contributor</h3>
                        <div className="contributor-card">
                            <img
                                src={mosque.contributor.picture || DEFAULT_AVATAR}
                                alt={mosque.contributor.name}
                                onError={(e) => {
                                    e.target.src = DEFAULT_AVATAR;
                                    e.target.onerror = null;
                                }}
                            />
                            <div>
                                <div className="info-item">
                                    <User size={18} />
                                    <strong>Name:</strong> {mosque.contributor.name}
                                </div>
                                <div className="info-item">
                                    <strong>Email:</strong> {mosque.contributor.email}
                                </div>
                                <div className="info-item">
                                    <Calendar size={18} />
                                    <strong>Added:</strong> {new Date(mosque.contributor.date).toLocaleDateString()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {(mosque.visitStats || mosque.directionClicks !== undefined) && (
                    <div className="section">
                        <h3>Visit Statistics</h3>
                        <div className="stats-grid">
                            <div className="stat-card">
                                <strong>Total Visits</strong>
                                <span>{mosque.visitStats?.totalVisits || 0}</span>
                            </div>
                            <div className="stat-card">
                                <strong>Today</strong>
                                <span>{mosque.visitStats?.todayVisits || 0}</span>
                            </div>
                            <div className="stat-card">
                                <strong>This Month</strong>
                                <span>{mosque.visitStats?.thisMonthVisits || 0}</span>
                            </div>
                            <div className="stat-card">
                                <strong>Directions Clicked</strong>
                                <span>{mosque.directionClicks ?? 0}</span>
                            </div>
                        </div>
                    </div>
                )}

                {mosque.comments && mosque.comments.length > 0 && (
                    <div className="section">
                        <h3>
                            <MessageCircle size={20} />
                            Comments ({mosque.comments.length})
                        </h3>
                        <div className="comments-list">
                            {mosque.comments.map((comment, index) => (
                                <div key={comment._id || index} className={`comment-card ${comment.hidden ? 'comment-card--hidden' : ''}`}>
                                    <div className="comment-header">
                                        {comment.picture && (
                                            <img src={comment.picture} alt={comment.name} />
                                        )}
                                        <div className="comment-meta">
                                            <strong>{comment.name}</strong>
                                            <span>{new Date(comment.date).toLocaleDateString()}</span>
                                        </div>
                                        <div className="comment-actions">
                                            <button
                                                type="button"
                                                className="action-btn"
                                                onClick={() => handleToggleCommentHide(comment._id)}
                                                disabled={hidingCommentId === comment._id}
                                                title={comment.hidden ? 'Unhide comment' : 'Hide comment'}
                                            >
                                                {comment.hidden ? <Eye size={16} /> : <EyeOff size={16} />}
                                            </button>
                                            <button
                                                type="button"
                                                className="delete-comment-btn"
                                                onClick={() => handleDeleteComment(comment._id)}
                                                disabled={deletingCommentId === comment._id}
                                                title="Delete comment"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <p>{comment.content}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {mosque.images && mosque.images.length > 0 && (
                    <div className="section">
                        <h3>
                            <ImageIcon size={20} />
                            Images ({mosque.images.length})
                        </h3>
                        <div className="images-grid">
                            {mosque.images.map((image, index) => (
                                <div key={index} className={`image-item-with-delete ${image.hidden ? 'image-item--hidden' : ''}`}>
                                    <img src={image.urlSmall || image.url} alt={`${mosque.name} ${index + 1}`} />
                                    <div className="image-actions">
                                        <button
                                            type="button"
                                            className="action-btn"
                                            onClick={() => handleToggleImageHide(index)}
                                            disabled={hidingImageIndex === index}
                                            title={image.hidden ? 'Unhide image' : 'Hide image'}
                                        >
                                            {image.hidden ? <Eye size={16} /> : <EyeOff size={16} />}
                                        </button>
                                        <button
                                            type="button"
                                            className="delete-image-btn"
                                            onClick={() => handleDeleteImage(index)}
                                            disabled={deletingIndex === index}
                                            title="Delete image"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {rejectModal && (
                <div className="modal-overlay" onClick={() => !updatingStatus && setRejectModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Reject Mosque</h3>
                        <p>This will mark the mosque as rejected.</p>
                        <div className="form-group">
                            <label>Reason (optional)</label>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason for rejection..."
                                rows={3}
                            />
                        </div>
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={rejectSendEmail}
                                onChange={(e) => setRejectSendEmail(e.target.checked)}
                            />
                            Send email to contributor
                        </label>
                        <div className="modal-actions">
                            <button onClick={() => setRejectModal(false)} disabled={updatingStatus}>Cancel</button>
                            <button className="btn-danger" onClick={handleReject} disabled={updatingStatus}>
                                {updatingStatus ? 'Rejecting...' : 'Reject'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {copyOverlayOpen && (
                <div className="modal-overlay" onClick={() => !copyingForYear && closeCopyOverlay()}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Add to {currentYear} List</h3>
                        <p>Update details before creating the copy:</p>
                        <div className="form-group">
                            <label>Name</label>
                            <input
                                type="text"
                                value={copyOverlayForm.name}
                                onChange={(e) => setCopyOverlayForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Mosque name"
                            />
                        </div>
                        <div className="form-group">
                            <label>Food / Dish</label>
                            <select
                                value={copyOverlayForm.dish}
                                onChange={(e) => setCopyOverlayForm(prev => ({ ...prev, dish: e.target.value }))}
                            >
                                {DISH_OPTIONS.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Description (optional)</label>
                            <textarea
                                value={copyOverlayForm.description}
                                onChange={(e) => setCopyOverlayForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Description"
                                rows={3}
                            />
                        </div>
                        <div className="modal-actions">
                            <button onClick={closeCopyOverlay} disabled={copyingForYear}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={handleCopyOverlaySubmit}
                                disabled={copyingForYear || !copyOverlayForm.name?.trim()}
                            >
                                {copyingForYear ? 'Creating...' : 'Create Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {clarificationModal && (
                <div className="modal-overlay" onClick={() => !updatingStatus && setClarificationModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h3>Ask for Clarification</h3>
                        <p>Send an email to the contributor requesting additional or corrected information.</p>
                        <div className="form-group">
                            <label>Notes / Remarks *</label>
                            <textarea
                                value={clarificationRemarks}
                                onChange={(e) => setClarificationRemarks(e.target.value)}
                                placeholder="Describe what information is missing or needs clarification..."
                                rows={4}
                                required
                            />
                        </div>
                        <div className="modal-actions">
                            <button onClick={() => setClarificationModal(false)} disabled={updatingStatus}>Cancel</button>
                            <button
                                className="btn-primary"
                                onClick={handleClarification}
                                disabled={updatingStatus || !clarificationRemarks.trim()}
                            >
                                {updatingStatus ? 'Sending...' : 'Send Email'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MosqueDetails;
